import { useState } from "react";
import { Box, ButtonBase, Dialog, Typography } from "@mui/material";
import { marketingLiftSx } from "@/shared/theme";
import type { HELP_SCREENSHOTS } from "./help-content";

type Shot = (typeof HELP_SCREENSHOTS)[number];

export const HelpProductFigure = ({ shot, compact = false }: { shot: Shot; compact?: boolean }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <Box
        component="figure"
        data-help-figure={compact ? "compact" : "full"}
        sx={{ m: compact ? 0 : "32px 0 0", width: "100%", maxWidth: compact ? 680 : "none", justifySelf: "stretch" }}
      >
        <ButtonBase
          aria-label={`Open product view: ${shot.alt}`}
          onClick={() => setOpen(true)}
          sx={{
            display: "block",
            width: "100%",
            textAlign: "left",
            border: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
            ...marketingLiftSx(),
            "&:hover, &:focus-visible": {
              ...marketingLiftSx()["&:hover, &:focus-visible"],
              borderColor: "primary.main",
            },
          }}
        >
          <Box
            component="img"
            src={shot.src}
            alt={shot.alt}
            width={shot.width}
            height={shot.height}
            loading="lazy"
            sx={{ display: "block", width: "100%", height: "auto", objectFit: "contain" }}
          />
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              gap: 2,
              alignItems: "center",
              p: 1.5,
              borderTop: "1px solid",
              borderColor: "divider",
            }}
          >
            <Typography component="figcaption" sx={{ color: "text.secondary", fontSize: ".68rem", lineHeight: 1.5 }}>
              {shot.caption}
            </Typography>
            <Typography
              aria-hidden
              sx={{ color: "secondary.main", flexShrink: 0, fontSize: ".62rem", fontWeight: 950 }}
            >
              OPEN VIEW ↗
            </Typography>
          </Box>
        </ButtonBase>
      </Box>
      <Dialog
        open={open}
        onClose={() => setOpen(false)}
        fullScreen
        fullWidth
        PaperProps={{
          square: true,
          sx: { m: 0, bgcolor: "background.default", backgroundImage: "none", overflow: "hidden" },
        }}
      >
        <Box
          role="document"
          sx={{
            height: "100dvh",
            minWidth: 0,
            display: "grid",
            gridTemplateRows: "52px minmax(0,1fr) auto",
            bgcolor: "background.default",
          }}
        >
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              px: 2,
              borderBottom: "2px solid",
              borderColor: "secondary.main",
            }}
          >
            <Typography sx={{ fontSize: ".66rem", fontWeight: 950, letterSpacing: ".1em" }}>
              CHESSLA / PRODUCT VIEW
            </Typography>
            <ButtonBase
              onClick={() => setOpen(false)}
              sx={{ minWidth: 52, minHeight: 44, color: "primary.main", fontSize: ".66rem", fontWeight: 950 }}
            >
              CLOSE ×
            </ButtonBase>
          </Box>
          <Box sx={{ minHeight: 0, display: "grid", placeItems: "center", overflow: "auto", p: { xs: 1, md: 3 } }}>
            <Box
              component="img"
              src={shot.src}
              alt={shot.alt}
              width={shot.width}
              height={shot.height}
              sx={{
                display: "block",
                maxWidth: "100%",
                maxHeight: "100%",
                width: "auto",
                height: "auto",
                objectFit: "contain",
              }}
            />
          </Box>
          <Typography
            sx={{ p: 2, borderTop: "1px solid", borderColor: "divider", color: "text.secondary", fontSize: ".72rem" }}
          >
            {shot.caption}
          </Typography>
        </Box>
      </Dialog>
    </>
  );
};
